import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { translateBackend } from 'assets/i18n/config';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import CustomFieldRenderer from 'ovComponents/3-pattern/customField/customFieldRenderer';
import { kebabCase } from 'lodash';

import {
  Button, Typography, Form, Box,
  TableCell,
  TableBody,
  TableRow,
  Table,
  TableHeadCell,
} from '../../../..';
import { invalidFields } from '../utils';

export const ApprovalVisual = ({
  options,
  loading,
  continueFunc,
  activeCustomFields,
  approvalData,
  updateApproval,
  ignoreCustomFieldTriggerRules,
}: {
  options: any;
  loading: boolean;
  continueFunc: any;
  activeCustomFields?: string[];
  approvalData?: any;
  updateApproval?: any;
  ignoreCustomFieldTriggerRules?: boolean;
}) => {
  const { t } = useTranslation(['workflowCompletions']);
  const [focused, setFocused] = useState<string[]>([]);
  const submit = () => {
    const fields = invalidFields({
      ...options,
    }, { ...approvalData });
    setFocused(fields);
    if (fields.length === 0) {
      continueFunc();
    }
  };

  const getMarkdown = (): string => {
    if (options?.filledTemplate) {
      return translateBackend(options?.filledTemplate);
    }
    return translateBackend(options?.template);
  };

  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);

  useEffect(() => {
    const customOptions: any[] = [];
    Object.keys(options).forEach((key: string) => {
      if (options[key]?.customField && options[key].enabled && activeCustomFields?.includes(key)) {
        customOptions.push(options[key]);
      }
    });
    setCustomOptionSelected(customOptions);
  }, [options, activeCustomFields]);

  return (
    <Form onSubmit={submit}>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      <Typography variant='bodyLarge' component={'div'} sx={{ mb: 3, table: { width: '100%' } }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          linkTarget={'_blank'}
          components={{
            table: ({ node, ...props }) => <Table {...props} sx={{ tableLayout: 'auto', width: '100%' }} />,
            thead: ({ node, ...props }) => <TableBody {...props} />,
            th: ({ node, ...props }) => {
              const { align, ...tableProps } = props;
              return (
                <TableHeadCell
                  {...tableProps}
                  align={align === 'char' ? 'left' : align}
                  sx={{
                    minWidth: '100px',
                    paddingLeft: 0,
                  }}
                />
              );
            },
            td: ({ node, ...props }) => {
              const { align, ...tableProps } = props;
              return (
                <TableCell
                  {...tableProps}
                  align={align === 'char' ? 'left' : align}
                  sx={{
                    maxWidth: '100px',
                    minWidth: '100px',
                    paddingLeft: 0,
                  }}
                />
              );
            },
            tr: ({ node, ...props }) => <TableRow {...props} />,
            tbody: ({ node, ...props }) => <TableBody {...props} />,
          }}
        >
          {getMarkdown()}
        </ReactMarkdown>
      </Typography>
      {customOptionSelected && customOptionSelected.length > 0 && (
        <CustomFieldRenderer
          customOptions={customOptionSelected}
          customData={approvalData}
          update={updateApproval}
          focused={focused}
          setFocused={setFocused}
          loading={loading}
          ignoreCustomFieldTriggerRules={ignoreCustomFieldTriggerRules}
        />
      )}
      {(!options?.enableCallToActions || !options?.callToActions || options?.callToActions.length === 0) && (
        <Box display='flex' justifyContent='end'>
          <Button
            dataTestId={`approval-${kebabCase(options?.title?.en)}-continue-button`}
            label={t('workflowCompletions:continue')}
            disabled={loading}
            sx={{ mt: 3, textAlign: 'center' }}
            type='submit'
          />
        </Box>
      )}
      {options?.enableCallToActions && options?.callToActions && options?.callToActions.length > 0 && (
        <Box display='flex' justifyContent='end' gap={2}>
          {options.callToActions.map((action: any, index: number) => (
            <Button
              key={index}
              dataTestId={`approval-${kebabCase(options?.title?.en)}-action-${index}-button`}
              label={translateBackend(action.label)}
              variant={action.variant}
              color={action.color}
              disabled={loading}
              sx={{ mt: 3, textAlign: 'center' }}
              onClick={() => {
                continueFunc(`branch-${index.toString()}`);
              }}
            />
          ))}
        </Box>
      )}
    </Form>
  );
};

export default ApprovalVisual;
